
import { useQuery } from '@tanstack/react-query';
import { invokeEdgeFunction } from '@/lib/edge-functions';
import { useAuth } from '@/context/AuthContext';
import { CURRENT_SEASON } from '@/lib/season';

export interface SplitRow {
  user_id: string;
  name: string;
  team_name?: string | null;
  pts: number;
  mp: number;
  w: number;
  d: number;
  l: number;
  f: number;
  rc: number;
  dd: number;
}

export interface StandingsSplitResponse {
  apertura: SplitRow[];
  clausura: SplitRow[];
  overall: SplitRow[];
  lastUpdated: string;
}

export const useStandingsSplit = (season: string = CURRENT_SEASON) => {
  const { currentMembership } = useAuth();
  const leagueId = currentMembership?.league_id;

  return useQuery<StandingsSplitResponse>({
    queryKey: ['standings-split', season, leagueId],
    queryFn: async () => {
      console.log('📊 Fetching standings split for league:', leagueId, 'season:', season);
      const { data, error } = await invokeEdgeFunction('get-standings-split', {
        body: { season, leagueId },
      });
      if (error) {
        console.error('❌ Error fetching standings split:', error);
        throw new Error(error.message);
      }
      console.log('✅ Standings split data:', {
        apertura: data?.apertura?.length || 0,
        clausura: data?.clausura?.length || 0,
        overall: data?.overall?.length || 0,
      });
      return data as StandingsSplitResponse;
    },
    enabled: !!leagueId,
    staleTime: 60 * 1000,
    refetchInterval: 60 * 1000,
    // Keep showing previous data while refetching fails — user never sees a blank page
    placeholderData: (prev) => prev,
    // Don't retry on auth errors - they won't resolve themselves
    retry: (failureCount, error) => {
      const errorMessage = String(error);
      if (errorMessage.includes('Unauthorized') || errorMessage.includes('401')) {
        return false;
      }
      return failureCount < 2;
    },
  });
};
